// DAS-2096: Portfolio Analysis page with universal dropzone and LLM-based file processing
import React, { useState, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, Table, Tag, Spin, Alert, Typography, Space, Statistic, Row, Col, Progress, Tooltip } from 'antd';
import {
  UploadCloud, FileText, CheckCircle, AlertTriangle,
  Building2, TrendingUp, DollarSign, BarChart3, Info,
  Clock, Cpu, CircleDollarSign, Target, Layers,
  ChevronDown, ChevronRight, Shield, PieChart,
  Activity, ArrowLeft, Leaf, Lock, Zap, Eye, EyeOff,
} from 'lucide-react';
import api from '../functions/api';
import PortfolioAnalysisView from './PortfolioAnalysisView';
import '../css/PortfolioAnalysis.css';

const { Title, Text, Paragraph } = Typography;

const PortfolioAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState(new Set(['companies', 'etf_stocks', 'etf_leftovers', 'operations']));

  const toggleSection = (key) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const SectionHeader = ({ sectionKey, icon, label, badge }) => (
    <div
      style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 8 }}
      onClick={() => toggleSection(sectionKey)}
    >
      {collapsedSections.has(sectionKey)
        ? <ChevronRight size={16} style={{ flexShrink: 0 }} />
        : <ChevronDown size={16} style={{ flexShrink: 0 }} />
      }
      {icon}
      <span>{label}</span>
      {badge}
    </div>
  );

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles.length) return;
    const file = acceptedFiles[0];
    setFileName(file.name);
    setLoading(true);
    setResult(null);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/portfolio_analysis', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data.data);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Analysis failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  // Build columns dynamically based on what data is available
  const companies = useMemo(() => {
    const raw = result?.companies || [];
    return raw.map(c => {
      if (c.asset_type === 'MUTUALFUND') {
        const name = (c.Company || '').toUpperCase();
        if (name.includes('ETF') || name.includes('UCITS') || name.includes('TRACKER')) {
          return { ...c, asset_type: 'ETF' };
        }
      }
      return c;
    });
  }, [result]);
  const summary = result?.file_summary;
  const isTimeSeries = summary?.file_type === 'time_series';

  const toNum = (v) => {
    if (v == null) return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
  };

  const confidenceColors = { A: '#52c41a', B: '#73d13d', C: '#faad14', D: '#fa8c16', E: '#ff4d4f' };

  const confidenceTag = (score, grade) => {
    if (score == null) return <Text type="secondary">—</Text>;
    return (
      <Tag color={confidenceColors[grade] || '#999'} style={{ fontWeight: 600, minWidth: 44, textAlign: 'center' }}>
        {grade || '?'} ({score})
      </Tag>
    );
  };

  const ConfHeader = ({ label, tooltip }) => (
    <Tooltip
      title={tooltip}
      placement="bottom"
      overlayStyle={{ maxWidth: 340 }}
      overlayInnerStyle={{ padding: '10px 14px' }}
    >
      <span style={{ cursor: 'help', whiteSpace: 'nowrap' }}>
        {label} <Info size={11} style={{ verticalAlign: 'middle', opacity: 0.6, marginLeft: 2 }} />
      </span>
    </Tooltip>
  );

  const nameConfTooltip = (
    <div style={{ fontSize: 12, lineHeight: '20px' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>How company name confidence is calculated:</div>
      <div style={{ marginBottom: 3 }}><Tag color="#52c41a" style={{ fontSize: 10 }}>A 90</Tag> Read from a structured column in CSV/Excel</div>
      <div style={{ marginBottom: 3 }}><Tag color="#faad14" style={{ fontSize: 10 }}>C 55-60</Tag> Vision-extracted (verified) or name-only entry</div>
      <div><Tag color="#fa8c16" style={{ fontSize: 10 }}>D 45</Tag> Vision OCR, freetext LLM, or name-lookup</div>
    </div>
  );

  const codeConfTooltip = (
    <div style={{ fontSize: 12, lineHeight: '20px' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>How code confidence is calculated:</div>
      <div style={{ marginBottom: 3 }}><Tag color="#52c41a" style={{ fontSize: 10 }}>A 90-95</Tag> ISIN/CUSIP/SEDOL checkdigit validated + resolved via API</div>
      <div style={{ marginBottom: 3 }}><Tag color="#73d13d" style={{ fontSize: 10 }}>B 78-82</Tag> Named identifier column or LLM-verified value</div>
      <div style={{ marginBottom: 3 }}><Tag color="#faad14" style={{ fontSize: 10 }}>C 55-65</Tag> LLM-mapped column or vision-verified</div>
      <div><Tag color="#fa8c16" style={{ fontSize: 10 }}>D 40-50</Tag> Vision OCR or unverified</div>
    </div>
  );

  const tickerConfTooltip = (
    <div style={{ fontSize: 12, lineHeight: '20px' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>How ticker confidence is calculated:</div>
      <div style={{ marginBottom: 3 }}><Tag color="#52c41a" style={{ fontSize: 10 }}>A 86-95</Tag> Resolved from validated ISIN/CUSIP/SEDOL, Bloomberg/RIC translated</div>
      <div style={{ marginBottom: 3 }}><Tag color="#73d13d" style={{ fontSize: 10 }}>B 70-85</Tag> Named ticker column in structured file, or verified via API</div>
      <div style={{ marginBottom: 3 }}><Tag color="#faad14" style={{ fontSize: 10 }}>C 55-69</Tag> LLM-identified column, time series header, or vision-verified</div>
      <div style={{ marginBottom: 3 }}><Tag color="#fa8c16" style={{ fontSize: 10 }}>D 40-54</Tag> Vision OCR, LLM name lookup, or freetext extraction</div>
      <div><Tag color="#999" style={{ fontSize: 10 }}>—</Tag> No ticker identified (only code or name found)</div>
    </div>
  );

  const posConfTooltip = (
    <div style={{ fontSize: 12, lineHeight: '20px' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>How position confidence is calculated:</div>
      <div style={{ marginBottom: 3 }}><Tag color="#73d13d" style={{ fontSize: 10 }}>B 85</Tag> Read from structured file (CSV/Excel cell value)</div>
      <div style={{ marginBottom: 3 }}><Tag color="#faad14" style={{ fontSize: 10 }}>C 55</Tag> Vision-extracted and ticker verified via API</div>
      <div><Tag color="#fa8c16" style={{ fontSize: 10 }}>D 40</Tag> Vision OCR or freetext LLM extraction</div>
    </div>
  );

  const currConfTooltip = (
    <div style={{ fontSize: 12, lineHeight: '20px' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>How currency confidence is calculated:</div>
      <div style={{ marginBottom: 3 }}><Tag color="#52c41a" style={{ fontSize: 10 }}>A 95</Tag> Enriched via yfinance market data lookup</div>
      <div style={{ marginBottom: 3 }}><Tag color="#52c41a" style={{ fontSize: 10 }}>A 90</Tag> Read from dedicated column in structured file</div>
      <div style={{ marginBottom: 3 }}><Tag color="#faad14" style={{ fontSize: 10 }}>C 55</Tag> Vision-extracted and ticker verified</div>
      <div><Tag color="#fa8c16" style={{ fontSize: 10 }}>D 40</Tag> Vision OCR or freetext LLM extraction</div>
    </div>
  );

  const assetConfTooltip = (
    <div style={{ fontSize: 12, lineHeight: '20px' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>How asset type confidence is calculated:</div>
      <div><Tag color="#52c41a" style={{ fontSize: 10 }}>A 95</Tag> Enriched via yfinance market data (EQUITY, ETF, etc.)</div>
    </div>
  );

  const getPosition = (record) => {
    const size = toNum(record.Position);
    const val = toNum(record.Value_eur);
    const net = toNum(record.net_shares);
    const exp = toNum(record.exposure_pct);
    if (size != null && size !== 0) return `${size.toLocaleString()} units`;
    if (val != null && val !== 0) return val.toLocaleString(undefined, { minimumFractionDigits: 2 });
    if (net != null && net !== 0) return `${net.toLocaleString()} net`;
    if (exp != null && exp !== 0) return `${exp.toFixed(2)}%`;
    return null;
  };

  const columns = [
    {
      title: '#',
      key: 'rank',
      width: 45,
      render: (_, __, idx) => <Text type="secondary">{idx + 1}</Text>,
    },
    {
      title: 'Company',
      dataIndex: 'Company',
      key: 'Company',
      ellipsis: true,
      render: (text) => <Text strong>{text || '—'}</Text>,
    },
    {
      title: <ConfHeader label="Name Conf." tooltip={nameConfTooltip} />,
      dataIndex: 'name_confidence',
      key: 'name_confidence',
      width: 115,
      sorter: (a, b) => (a.name_confidence || 0) - (b.name_confidence || 0),
      render: (score, record) => confidenceTag(score, record.name_confidence_grade),
    },
    {
      title: 'Code',
      key: 'code',
      width: 130,
      render: (_, record) => {
        const code = record.code;
        if (!code) return <Text type="secondary">—</Text>;
        const ctype = (record.code_type || 'code').toUpperCase();
        return (
          <Space size={4}>
            <Tag color={ctype === 'ISIN' ? 'purple' : ctype === 'CUSIP' ? 'magenta' : 'blue'} style={{ fontSize: 10, lineHeight: '16px' }}>{ctype}</Tag>
            <Text style={{ fontSize: 12 }}>{code}</Text>
          </Space>
        );
      },
    },
    {
      title: <ConfHeader label="Code Conf." tooltip={codeConfTooltip} />,
      dataIndex: 'code_confidence',
      key: 'code_confidence',
      width: 115,
      sorter: (a, b) => (a.code_confidence || 0) - (b.code_confidence || 0),
      render: (score, record) => confidenceTag(score, record.code_confidence_grade),
    },
    {
      title: 'Ticker',
      dataIndex: 'Ticker',
      key: 'Ticker',
      width: 100,
      render: (v) => v ? <Text code>{v}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: <ConfHeader label="Ticker Conf." tooltip={tickerConfTooltip} />,
      dataIndex: 'ticker_confidence',
      key: 'ticker_confidence',
      width: 115,
      sorter: (a, b) => (a.ticker_confidence || 0) - (b.ticker_confidence || 0),
      filters: [
        { text: 'A (≥86)', value: 'A' },
        { text: 'B (70-85)', value: 'B' },
        { text: 'C (55-69)', value: 'C' },
        { text: 'D (40-54)', value: 'D' },
        { text: 'No ticker', value: 'none' },
      ],
      onFilter: (value, record) =>
        value === 'none' ? !record.ticker_confidence : record.ticker_confidence_grade === value,
      render: (score, record) => confidenceTag(score, record.ticker_confidence_grade),
    },
    {
      title: 'Position',
      key: 'position',
      align: 'right',
      render: (_, record) => {
        const pos = getPosition(record);
        return pos ? <Text>{pos}</Text> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: <ConfHeader label="Pos. Conf." tooltip={posConfTooltip} />,
      dataIndex: 'position_confidence',
      key: 'position_confidence',
      width: 110,
      sorter: (a, b) => (a.position_confidence || 0) - (b.position_confidence || 0),
      render: (score, record) => confidenceTag(score, record.position_confidence_grade),
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      width: 85,
      filters: [...new Set(companies.map(c => c.currency).filter(Boolean))].map(v => ({ text: v, value: v })),
      onFilter: (value, record) => record.currency === value,
      render: (v) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: <ConfHeader label="Curr. Conf." tooltip={currConfTooltip} />,
      dataIndex: 'currency_confidence',
      key: 'currency_confidence',
      width: 110,
      sorter: (a, b) => (a.currency_confidence || 0) - (b.currency_confidence || 0),
      render: (score, record) => confidenceTag(score, record.currency_confidence_grade),
    },
    {
      title: 'Asset Type',
      dataIndex: 'asset_type',
      key: 'asset_type',
      filters: [...new Set(companies.map(c => c.asset_type).filter(Boolean))].map(v => ({ text: v, value: v })),
      onFilter: (value, record) => record.asset_type === value,
      render: (v, record) => {
        const hasHoldings = record.top_holdings || record.bond_ratings;
        if (!v) return <Text type="secondary">—</Text>;
        const isExpanded = expandedRowKeys.includes(record.key);
        return (
          <Space size={4}>
            <Tag color="geekblue">{v}</Tag>
            {hasHoldings && (
              <Tag
                color={isExpanded ? 'blue' : 'cyan'}
                style={{ cursor: 'pointer', fontSize: 10, lineHeight: '16px', userSelect: 'none' }}
                onClick={() => {
                  setExpandedRowKeys((prev) =>
                    prev.includes(record.key)
                      ? prev.filter((k) => k !== record.key)
                      : [...prev, record.key]
                  );
                }}
              >
                <Layers size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                {isExpanded ? 'Hide' : 'Holdings'}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: <ConfHeader label="Asset Conf." tooltip={assetConfTooltip} />,
      dataIndex: 'asset_type_confidence',
      key: 'asset_type_confidence',
      width: 110,
      sorter: (a, b) => (a.asset_type_confidence || 0) - (b.asset_type_confidence || 0),
      render: (score, record) => confidenceTag(score, record.asset_type_confidence_grade),
    },
  ].filter(Boolean);

  const hasAnyHoldings = companies.some(c => c.top_holdings || c.bond_ratings);

  const holdingsColumns = [
    { title: '#', key: 'idx', width: 36, render: (_, __, i) => <Text type="secondary">{i + 1}</Text> },
    { title: 'Symbol', dataIndex: 'symbol', key: 'symbol', width: 100,
      render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
    { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: 'Weight', dataIndex: 'holding_percent', key: 'pct', width: 100, align: 'right',
      render: (v) => <Text strong>{v?.toFixed(2)}%</Text> },
    { title: '', key: 'bar', width: 120,
      render: (_, r) => <Progress percent={r.holding_percent} size="small" showInfo={false}
        strokeColor="#1890ff" style={{ margin: 0 }} /> },
  ];

  const sectorLabels = {
    technology: 'Technology', financial_services: 'Financial Services', healthcare: 'Healthcare',
    consumer_cyclical: 'Consumer Cyclical', industrials: 'Industrials', communication_services: 'Communication',
    consumer_defensive: 'Consumer Defensive', energy: 'Energy', basic_materials: 'Basic Materials',
    realestate: 'Real Estate', utilities: 'Utilities',
  };

  const renderExpandedRow = (record) => {
    const holdings = record.top_holdings;
    const bondRatings = record.bond_ratings;
    const bondHoldings = record.bond_holdings;
    const sectors = record.sector_weightings;
    const mix = record.asset_mix;

    return (
      <div style={{ padding: '8px 0' }}>
        <Row gutter={24}>
          {holdings?.length > 0 && (
            <Col xs={24} lg={14}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Top {holdings.length} Holdings
                <Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
                  ({holdings.reduce((s, h) => s + h.holding_percent, 0).toFixed(1)}% of fund)
                </Text>
              </Text>
              <Table
                dataSource={holdings.map((h, i) => ({ ...h, key: i }))}
                columns={holdingsColumns}
                pagination={false}
                size="small"
                style={{ marginBottom: 16 }}
              />
            </Col>
          )}

          {bondRatings && Object.keys(bondRatings).length > 0 && (
            <Col xs={24} lg={holdings?.length ? 10 : 14}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Bond Credit Ratings</Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Object.entries(bondRatings)
                  .sort(([, a], [, b]) => b - a)
                  .map(([rating, pct]) => (
                    <Tag key={rating} color="blue" style={{ fontSize: 12 }}>
                      {rating.toUpperCase()} {pct.toFixed(1)}%
                    </Tag>
                  ))}
              </div>
              {bondHoldings && (
                <div style={{ marginTop: 12 }}>
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>Bond Metrics</Text>
                  <Space size={16}>
                    {bondHoldings.duration != null && (
                      <Text>Duration: <Text strong>{bondHoldings.duration} yrs</Text></Text>
                    )}
                    {bondHoldings.maturity != null && (
                      <Text>Avg Maturity: <Text strong>{bondHoldings.maturity} yrs</Text></Text>
                    )}
                  </Space>
                </div>
              )}
            </Col>
          )}

          <Col xs={24} lg={holdings?.length ? 10 : 10}>
            {sectors && Object.keys(sectors).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Sector Breakdown</Text>
                {Object.entries(sectors)
                  .sort(([, a], [, b]) => b - a)
                  .map(([sector, pct]) => (
                    <div key={sector} style={{ display: 'flex', alignItems: 'center', marginBottom: 4, gap: 8 }}>
                      <Text style={{ fontSize: 12, width: 130, flexShrink: 0 }}>
                        {sectorLabels[sector] || sector}
                      </Text>
                      <Progress percent={pct} size="small" style={{ flex: 1, margin: 0 }}
                        format={(p) => `${p.toFixed(1)}%`}
                        strokeColor={pct > 20 ? '#1890ff' : pct > 10 ? '#69c0ff' : '#bae7ff'} />
                    </div>
                  ))}
              </div>
            )}

            {mix && Object.keys(mix).length > 0 && (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Asset Allocation</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {Object.entries(mix)
                    .filter(([, v]) => v > 0)
                    .sort(([, a], [, b]) => b - a)
                    .map(([cls, pct]) => {
                      const label = cls.replace('Position', '');
                      const colors = { stock: 'green', bond: 'blue', cash: 'default', preferred: 'purple', convertible: 'orange', other: 'default' };
                      return (
                        <Tag key={cls} color={colors[label] || 'default'}>
                          {label.charAt(0).toUpperCase() + label.slice(1)} {pct.toFixed(1)}%
                        </Tag>
                      );
                    })}
                </div>
              </div>
            )}
          </Col>
        </Row>
      </div>
    );
  };

  const resetAnalysis = () => {
    setResult(null);
    setError(null);
    setFileName(null);
    setExpandedRowKeys([]);
  };

  // DAS-2133: Start page — shown before file analysis
  if (!result) {
    return (
      <div className="pa-page pa-page-start">
        <section className="pa-hero">
          {/* Argo Navis constellation — the mission ship */}
          <svg className="pa-constellation" viewBox="0 0 600 400" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
              <filter id="pa-glow" x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="2.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="pa-glow-bright" x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="pa-glow-canopus" x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="7" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Constellation lines */}
            <g stroke="rgba(255,255,255,0.18)" strokeWidth="1">
              {/* Keel (Carina) — the curved bottom of the hull */}
              <line x1="80" y1="265" x2="160" y2="305" />
              <line x1="160" y1="305" x2="235" y2="330" />
              <line x1="235" y1="330" x2="315" y2="342" />
              <line x1="315" y1="342" x2="400" y2="325" />
              <line x1="400" y1="325" x2="468" y2="292" />
              <line x1="468" y1="292" x2="518" y2="248" />
              {/* Bow hull — prow down to keel */}
              <line x1="48" y1="168" x2="62" y2="215" />
              <line x1="62" y1="215" x2="80" y2="265" />
              {/* Stern hull — stern down to keel */}
              <line x1="528" y1="180" x2="530" y2="215" />
              <line x1="530" y1="215" x2="518" y2="248" />
              {/* Stern structure */}
              <line x1="405" y1="138" x2="472" y2="120" />
              <line x1="472" y1="120" x2="518" y2="105" />
              <line x1="518" y1="105" x2="540" y2="138" />
              <line x1="540" y1="138" x2="528" y2="180" />
              {/* Deck (Puppis) — the gunwale line */}
              <line x1="48" y1="168" x2="118" y2="155" />
              <line x1="118" y1="155" x2="190" y2="148" />
              <line x1="190" y1="148" x2="232" y2="145" />
              <line x1="232" y1="145" x2="328" y2="148" />
              <line x1="328" y1="148" x2="405" y2="138" />
              {/* Mast */}
              <line x1="232" y1="145" x2="230" y2="15" />
              {/* Yard (crossbeam) */}
              <line x1="162" y1="48" x2="230" y2="15" />
              <line x1="230" y1="15" x2="298" y2="45" />
              {/* Sail — triangular frame */}
              <line x1="148" y1="105" x2="162" y2="48" />
              <line x1="298" y1="45" x2="312" y2="102" />
              <line x1="148" y1="105" x2="312" y2="102" />
              {/* Upper sail detail */}
              <line x1="148" y1="105" x2="185" y2="72" />
              <line x1="275" y1="68" x2="312" y2="102" />
              <line x1="185" y1="72" x2="162" y2="48" />
              <line x1="298" y1="45" x2="275" y2="68" />
              {/* Rigging to deck */}
              <line x1="148" y1="105" x2="118" y2="155" />
              <line x1="312" y1="102" x2="328" y2="148" />
            </g>

            {/* Stars — Vela (sails) */}
            <g filter="url(#pa-glow)">
              <circle cx="148" cy="105" r="2.0" fill="rgba(220,235,255,0.55)" />
              <circle cx="185" cy="72" r="1.6" fill="rgba(220,235,255,0.4)" />
              <circle cx="275" cy="68" r="1.6" fill="rgba(220,235,255,0.4)" />
              <circle cx="312" cy="102" r="2.0" fill="rgba(220,235,255,0.55)" />
            </g>
            <g filter="url(#pa-glow-bright)">
              <circle cx="230" cy="15" r="2.5" fill="rgba(220,235,255,0.7)" />
              <circle cx="162" cy="48" r="2.2" fill="rgba(220,235,255,0.6)" />
              <circle cx="298" cy="45" r="2.2" fill="rgba(220,235,255,0.6)" />
            </g>

            {/* Stars — Puppis (deck & stern) */}
            <g filter="url(#pa-glow)">
              <circle cx="48" cy="168" r="2.0" fill="rgba(220,235,255,0.5)" />
              <circle cx="118" cy="155" r="1.6" fill="rgba(220,235,255,0.4)" />
              <circle cx="190" cy="148" r="1.5" fill="rgba(220,235,255,0.35)" />
              <circle cx="232" cy="145" r="1.6" fill="rgba(220,235,255,0.4)" />
              <circle cx="328" cy="148" r="1.6" fill="rgba(220,235,255,0.4)" />
              <circle cx="540" cy="138" r="1.6" fill="rgba(220,235,255,0.4)" />
              <circle cx="528" cy="180" r="1.5" fill="rgba(220,235,255,0.35)" />
            </g>
            <g filter="url(#pa-glow-bright)">
              <circle cx="405" cy="138" r="2.2" fill="rgba(220,235,255,0.6)" />
              <circle cx="472" cy="120" r="2.0" fill="rgba(220,235,255,0.55)" />
              <circle cx="518" cy="105" r="2.0" fill="rgba(220,235,255,0.55)" />
            </g>

            {/* Stars — Carina (keel) */}
            <g filter="url(#pa-glow)">
              <circle cx="62" cy="215" r="1.5" fill="rgba(220,235,255,0.35)" />
              <circle cx="530" cy="215" r="1.5" fill="rgba(220,235,255,0.35)" />
              <circle cx="518" cy="248" r="1.6" fill="rgba(220,235,255,0.4)" />
            </g>
            <g filter="url(#pa-glow-bright)">
              <circle cx="80" cy="265" r="2.2" fill="rgba(220,235,255,0.6)" />
              <circle cx="160" cy="305" r="2.0" fill="rgba(220,235,255,0.55)" />
              <circle cx="235" cy="330" r="2.2" fill="rgba(220,235,255,0.6)" />
              <circle cx="400" cy="325" r="2.2" fill="rgba(220,235,255,0.6)" />
              <circle cx="468" cy="292" r="2.0" fill="rgba(220,235,255,0.5)" />
            </g>

            {/* Canopus — α Carinae, second brightest star in the night sky */}
            <circle className="pa-canopus" cx="315" cy="342" r="4" fill="rgba(220,235,255,0.85)" filter="url(#pa-glow-canopus)" />

            {/* Star names */}
            <g fill="rgba(148,163,184,0.35)" fontSize="7.5" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="0.5">
              <text x="240" y="12">Alsephina</text>
              <text x="120" y="45">Regor</text>
              <text x="308" y="42">Suhail</text>
              <text x="415" y="134">Naos</text>
              <text x="526" y="100">Turais</text>
              <text x="44" y="261" textAnchor="end">Aspidiske</text>
              <text x="124" y="302" textAnchor="end">Avior</text>
              <text x="330" y="362">Canopus</text>
              <text x="410" y="340">Miaplacidus</text>
            </g>

            {/* Constellation name */}
            <text
              x="300" y="210"
              fill="rgba(148,163,184,0.13)"
              fontSize="14"
              fontFamily="system-ui, -apple-system, sans-serif"
              fontWeight="600"
              letterSpacing="6"
              textAnchor="middle"
            >ARGO NAVIS</text>

            {/* Sub-constellation labels */}
            <g fill="rgba(148,163,184,0.09)" fontSize="8.5" fontFamily="system-ui, -apple-system, sans-serif" fontWeight="500" letterSpacing="2.5">
              <text x="225" y="90" textAnchor="middle">VELA</text>
              <text x="460" y="160" textAnchor="middle">PUPPIS</text>
              <text x="280" y="295" textAnchor="middle">CARINA</text>
            </g>
          </svg>

          <div className="pa-hero-inner">
            <div className="pa-hero-eyebrow">Portfolio Intelligence</div>
            <h1 className="pa-hero-title">
              See your portfolio<br />the way institutions do.
            </h1>
            <p className="pa-hero-subtitle">
              Drop any file containing your holdings. Our AI identifies every position,
              verifies it against live market data, and delivers a comprehensive
              analysis — in seconds.
            </p>

            <div className="pa-hero-dropzone-wrapper">
              <div
                {...getRootProps()}
                className={`pa-dropzone pa-dropzone-hero ${isDragActive ? 'pa-dropzone-active' : ''} ${loading ? 'pa-dropzone-disabled' : ''}`}
              >
                <input {...getInputProps()} />
                {loading ? (
                  <div className="pa-dropzone-content">
                    <Spin size="large" />
                    <Text style={{ marginTop: 16, color: '#e2e8f0' }}>Analyzing {fileName}...</Text>
                    <Text style={{ color: 'rgba(226,232,240,0.6)' }}>This typically takes 10–30 seconds</Text>
                  </div>
                ) : (
                  <div className="pa-dropzone-content">
                    <UploadCloud size={44} strokeWidth={1.5} className="pa-upload-icon-hero" />
                    <Text strong style={{ fontSize: 17, color: '#e2e8f0' }}>
                      {isDragActive ? 'Drop it here' : 'Drag & drop your portfolio file'}
                    </Text>
                    <Text style={{ color: 'rgba(226,232,240,0.7)', fontSize: 13 }}>
                      or click to browse — CSV, Excel, PDF, JSON, TXT
                    </Text>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <Alert
                message="Analysis Failed"
                description={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginTop: 20, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto', textAlign: 'left' }}
              />
            )}

            <div className="pa-trust-bar">
              <div className="pa-trust-item">
                <Lock size={15} />
                <span>No sign-up required</span>
              </div>
              <div className="pa-trust-item">
                <Shield size={15} />
                <span>Processed in-memory, never saved to disk</span>
              </div>
              <div className="pa-trust-item">
                <EyeOff size={15} />
                <span>No cookies or tracking</span>
              </div>
              <div className="pa-trust-item">
                <Zap size={15} />
                <span>Free — a capability demo, not a product</span>
              </div>
            </div>
          </div>
        </section>

        <section className="pa-capabilities-section">
          <div className="pa-section-inner">
            <h2 className="pa-section-title">Your Mission Control</h2>
            <p className="pa-section-subtitle">
              Six instruments. One dashboard. Full command.
            </p>
            <div className="pa-cap-grid">
              <div className="pa-cap-card">
                <div className="pa-cap-icon"><Target size={24} /></div>
                <h3>Full Visibility</h3>
                <p>Every position identified and verified against live market data. Tickers, ISINs, company names — each with a confidence score.</p>
              </div>
              <div className="pa-cap-card">
                <div className="pa-cap-icon"><PieChart size={24} /></div>
                <h3>Balance Check</h3>
                <p>Stocks, bonds, ETFs, mutual funds — full allocation breakdown. Spot over-concentration before it becomes risk.</p>
              </div>
              <div className="pa-cap-card">
                <div className="pa-cap-icon"><BarChart3 size={24} /></div>
                <h3>Risk Radar</h3>
                <p>Sector concentration, geographic exposure, asset-class distribution — see where your capital actually sits.</p>
              </div>
              <div className="pa-cap-card">
                <div className="pa-cap-icon"><Leaf size={24} /></div>
                <h3>Values Compass</h3>
                <p>ESG scoring for every holding. Environmental, social, governance — know what you're backing.</p>
              </div>
              <div className="pa-cap-card">
                <div className="pa-cap-icon"><Activity size={24} /></div>
                <h3>Early Warning</h3>
                <p>Momentum shifts, price signals, trend changes — detect them before they catch you off guard.</p>
              </div>
              <div className="pa-cap-card">
                <div className="pa-cap-icon"><Eye size={24} /></div>
                <h3>Smart Money Tracker</h3>
                <p>Institutional ownership changes across your holdings. See what the big players are doing with your stocks.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="pa-how-section">
          <div className="pa-section-inner">
            <h2 className="pa-section-title">How it works</h2>
            <div className="pa-steps">
              <div className="pa-step">
                <div className="pa-step-number">1</div>
                <h3>Drop your file</h3>
                <p>Any broker export. DEGIRO, Interactive Brokers, Saxo, ABN AMRO — or any portfolio document.</p>
              </div>
              <div className="pa-step-divider" />
              <div className="pa-step">
                <div className="pa-step-number">2</div>
                <h3>AI analyzes everything</h3>
                <p>Securities identified and verified against live data. Enriched with fundamentals, ESG, and market intelligence.</p>
              </div>
              <div className="pa-step-divider" />
              <div className="pa-step">
                <div className="pa-step-number">3</div>
                <h3>Get your dashboard</h3>
                <p>Allocation, exposure, risk, momentum, institutional activity — a complete picture of your portfolio.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="pa-personal-section">
          <div className="pa-personal-inner">
            <h2 className="pa-personal-title">Who built this?</h2>
            <div className="pa-personal-text">
              <p>
                A retail investor, engineer, and AI enthusiast — not a company.
              </p>
              <p>
                There's no "About Us" page here. That's a deliberate choice, not an omission.
                This tool should speak for itself, and you shouldn't need to know my name to
                evaluate whether it's useful.
              </p>
              <p>
                If this sparks your curiosity — if you want to go deeper, connect, or discuss
                what trust and identity actually mean when you discover something online —
                I'll fully introduce myself. Until then, your anonymity is respected just as mine is.
              </p>
            </div>
          </div>
        </section>

        <footer className="pa-footer-note">
          <Shield size={14} />
          <span>Your data is processed in real-time and immediately discarded. Nothing is stored, shared, or tracked.</span>
        </footer>
      </div>
    );
  }

  // ANALYSIS RESULTS PAGE
  return (
    <div className="pa-page">
      <div className="pa-container">
        <div className="pa-header">
          <button className="pa-back-btn" onClick={resetAnalysis} type="button">
            <ArrowLeft size={18} />
            <span>Analyze another file</span>
          </button>
          <Title level={2} style={{ margin: '12px 0 0' }}>
            <BarChart3 size={28} style={{ marginRight: 10, verticalAlign: 'middle' }} />
            Portfolio Analysis
          </Title>
          <Paragraph type="secondary" style={{ marginTop: 4 }}>
            Results for <Text strong>{fileName}</Text>
          </Paragraph>
        </div>

        {result && summary && (
          <>
            {companies.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <PortfolioAnalysisView
                  data={companies}
                  source="file_drop"
                  etfStockPositions={result.etf_stock_positions || []}
                  etfLeftoverPositions={result.etf_leftover_positions || []}
                />
              </div>
            )}

            <Row gutter={16} style={{ marginTop: 24 }}>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Companies Found"
                    value={summary.total_companies}
                    prefix={<Building2 size={16} />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Positions"
                    value={summary.has_positions ? 'Yes' : 'No'}
                    prefix={<DollarSign size={16} />}
                    valueStyle={{ color: summary.has_positions ? '#52c41a' : '#999' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Performance"
                    value={summary.has_performance ? 'Yes' : 'No'}
                    prefix={<TrendingUp size={16} />}
                    valueStyle={{ color: summary.has_performance ? '#52c41a' : '#999' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Purchase Costs"
                    value={summary.has_purchase_costs ? 'Yes' : 'No'}
                    prefix={<CheckCircle size={16} />}
                    valueStyle={{ color: summary.has_purchase_costs ? '#52c41a' : '#999' }}
                  />
                </Card>
              </Col>
              {summary.has_exposure && (
                <Col xs={12} sm={6}>
                  <Card size="small">
                    <Statistic
                      title="Exposure Data"
                      value="Yes"
                      prefix={<BarChart3 size={16} />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Card>
                </Col>
              )}
              {summary.total_transactions && (
                <Col xs={12} sm={6}>
                  <Card size="small">
                    <Statistic
                      title="Total Transactions"
                      value={summary.total_transactions}
                      prefix={<FileText size={16} />}
                    />
                  </Card>
                </Col>
              )}
              {summary.has_dividends && (
                <Col xs={12} sm={6}>
                  <Card size="small">
                    <Statistic
                      title="Dividends"
                      value="Yes"
                      prefix={<DollarSign size={16} />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Card>
                </Col>
              )}
            </Row>

            {isTimeSeries && (
              <Alert
                message="Time Series Data Detected"
                description={`This file contains historical price data for ${summary.total_companies} security${summary.total_companies !== 1 ? 'ies' : ''}. Performance is calculated from first to last available data point.`}
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}

            {summary.file_type === 'transactions' && (
              <Alert
                message="Transaction Log Detected"
                description={`This file contains ${summary.total_transactions} transactions across ${summary.total_companies} unique securities. Data has been aggregated by ticker — showing net shares, average buy price, realized P&L, and dividends.`}
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}

            {summary.notes && (
              <Alert
                message="Analysis Notes"
                description={summary.notes}
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}

            <Card
              title={
                <SectionHeader
                  sectionKey="companies"
                  icon={<FileText size={16} />}
                  label={`Identified Companies — ${fileName}`}
                  badge={<Tag color="green">{summary.total_companies} found</Tag>}
                />
              }
              style={{ marginTop: 16 }}
              styles={collapsedSections.has('companies') ? { body: { display: 'none' } } : undefined}
            >
              <Table
                dataSource={result.companies?.map((c, i) => ({ ...c, key: i }))}
                columns={columns}
                expandable={hasAnyHoldings ? {
                  expandedRowRender: renderExpandedRow,
                  rowExpandable: (record) => !!(record.top_holdings || record.bond_ratings || record.asset_mix),
                  expandedRowKeys,
                  onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
                  expandIcon: () => null,
                  columnWidth: 0,
                  expandIconColumnIndex: -1,
                } : undefined}
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  pageSizeOptions: ['25', '50', '100', '200'],
                  showTotal: (total) => `${total} companies`,
                }}
                size="small"
                scroll={{ x: 1600 }}
              />
            </Card>

            {result.etf_stock_positions?.length > 0 && (
              <Card
                title={
                  <SectionHeader
                    sectionKey="etf_stocks"
                    icon={<Target size={16} />}
                    label="Stock Positions Within ETFs"
                    badge={<Tag color="blue">{result.etf_stock_positions.length} stocks</Tag>}
                  />
                }
                size="small"
                style={{ marginTop: 16 }}
                styles={collapsedSections.has('etf_stocks') ? { body: { display: 'none' } } : undefined}
              >
                <Table
                  dataSource={result.etf_stock_positions.map((p, i) => ({ ...p, key: i }))}
                  columns={[
                    {
                      title: 'Company',
                      dataIndex: 'name',
                      key: 'name',
                      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
                      render: (v) => <Text strong>{v}</Text>,
                    },
                    {
                      title: 'Ticker',
                      dataIndex: 'symbol',
                      key: 'symbol',
                      width: 100,
                      sorter: (a, b) => (a.symbol || '').localeCompare(b.symbol || ''),
                      render: (v) => <Tag>{v}</Tag>,
                    },
                    {
                      title: 'Currency',
                      dataIndex: 'currency',
                      key: 'currency',
                      width: 90,
                      filters: [...new Set(result.etf_stock_positions.map(p => p.currency).filter(Boolean))].map(v => ({ text: v, value: v })),
                      onFilter: (value, record) => record.currency === value,
                    },
                    {
                      title: 'Implied Value',
                      dataIndex: 'total_value',
                      key: 'total_value',
                      width: 130,
                      sorter: (a, b) => (a.total_value || 0) - (b.total_value || 0),
                      defaultSortOrder: 'descend',
                      render: (v, record) => (
                        <Text strong>
                          {record.currency === 'USD' ? '$' : record.currency === 'EUR' ? '€' : ''}{v?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Text>
                      ),
                    },
                    {
                      title: 'ETF(s)',
                      dataIndex: 'etfs',
                      key: 'etfs',
                      render: (etfs) => (
                        <Space size={[4, 4]} wrap>
                          {etfs?.map((e, i) => (
                            <Tooltip
                              key={i}
                              title={`${e.name}: ${e.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            >
                              <Tag color="geekblue">{e.ticker}</Tag>
                            </Tooltip>
                          ))}
                        </Space>
                      ),
                    },
                  ]}
                  pagination={{
                    pageSize: 25,
                    showSizeChanger: true,
                    pageSizeOptions: ['25', '50', '100'],
                    showTotal: (total) => `${total} stocks`,
                  }}
                  size="small"
                  scroll={{ x: 800 }}
                />
              </Card>
            )}

            {result.etf_leftover_positions?.length > 0 && (
              <Card
                title={
                  <SectionHeader
                    sectionKey="etf_leftovers"
                    icon={<BarChart3 size={16} />}
                    label="ETF Unidentified Portions"
                    badge={<Tag color="orange">{result.etf_leftover_positions.length} ETFs</Tag>}
                  />
                }
                size="small"
                style={{ marginTop: 16 }}
                styles={collapsedSections.has('etf_leftovers') ? { body: { display: 'none' } } : undefined}
              >
                <Table
                  dataSource={result.etf_leftover_positions.map((p, i) => ({ ...p, key: i }))}
                  columns={[
                    {
                      title: 'ETF',
                      dataIndex: 'name',
                      key: 'name',
                      render: (v, record) => (
                        <Space size={4}>
                          <Text strong>{v}</Text>
                          {record.ticker && <Tag>{record.ticker}</Tag>}
                        </Space>
                      ),
                    },
                    {
                      title: 'Currency',
                      dataIndex: 'currency',
                      key: 'currency',
                      width: 90,
                    },
                    {
                      title: 'Position Value',
                      dataIndex: 'position_value',
                      key: 'position_value',
                      width: 130,
                      sorter: (a, b) => (a.position_value || 0) - (b.position_value || 0),
                      render: (v) => v?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                    },
                    {
                      title: 'Top Holdings',
                      key: 'covered',
                      width: 140,
                      render: (_, record) => (
                        <Tooltip title={`${record.holdings_count} holdings identified`}>
                          <Text type="success">{record.top_holdings_pct}%</Text>
                        </Tooltip>
                      ),
                    },
                    {
                      title: 'Unidentified',
                      key: 'leftover',
                      width: 140,
                      sorter: (a, b) => (a.leftover_pct || 0) - (b.leftover_pct || 0),
                      render: (_, record) => (
                        <Text type="warning" strong>{record.leftover_pct}%</Text>
                      ),
                    },
                    {
                      title: 'Unidentified Value',
                      dataIndex: 'leftover_value',
                      key: 'leftover_value',
                      width: 150,
                      sorter: (a, b) => (a.leftover_value || 0) - (b.leftover_value || 0),
                      defaultSortOrder: 'descend',
                      render: (v) => (
                        <Text type="warning" strong>
                          {v?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Text>
                      ),
                    },
                  ]}
                  pagination={false}
                  size="small"
                  scroll={{ x: 700 }}
                  summary={(data) => {
                    const totalPos = data.reduce((s, r) => s + (r.position_value || 0), 0);
                    const totalLeft = data.reduce((s, r) => s + (r.leftover_value || 0), 0);
                    const avgPct = totalPos > 0 ? (totalLeft / totalPos * 100) : 0;
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                          <Table.Summary.Cell index={1} />
                          <Table.Summary.Cell index={2}>
                            <Text strong>{totalPos.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} />
                          <Table.Summary.Cell index={4}>
                            <Text type="warning" strong>{avgPct.toFixed(1)}%</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5}>
                            <Text type="warning" strong>{totalLeft.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </Card>
            )}

            {result._operations && (() => {
              const ops = result._operations;
              const sr = ops.success_rates || {};
              const rateTag = (r) => {
                if (!r) return <Text type="secondary">—</Text>;
                const color = r.pct >= 80 ? '#52c41a' : r.pct >= 50 ? '#faad14' : '#ff4d4f';
                return <Text style={{ color, fontWeight: 600 }}>{r.found}/{r.total} ({r.pct}%)</Text>;
              };
              return (
                <Card
                  title={
                    <SectionHeader
                      sectionKey="operations"
                      icon={<Cpu size={16} />}
                      label="Operations"
                    />
                  }
                  size="small"
                  style={{ marginTop: 16 }}
                  styles={collapsedSections.has('operations') ? { body: { display: 'none' } } : undefined}
                >
                  <Row gutter={[16, 12]}>
                    <Col xs={12} sm={6}>
                      <Statistic
                        title="Duration"
                        value={ops.duration_seconds}
                        suffix="s"
                        prefix={<Clock size={14} />}
                        valueStyle={{ fontSize: 18 }}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <Statistic
                        title="LLM Calls"
                        value={ops.llm_calls}
                        prefix={<Cpu size={14} />}
                        valueStyle={{ fontSize: 18 }}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <Statistic
                        title="Total Tokens"
                        value={ops.total_tokens?.toLocaleString()}
                        valueStyle={{ fontSize: 18 }}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <Statistic
                        title="Est. Cost"
                        value={`$${ops.estimated_cost_usd?.toFixed(4)}`}
                        prefix={<CircleDollarSign size={14} />}
                        valueStyle={{ fontSize: 18 }}
                      />
                    </Col>
                  </Row>

                  {ops.calls_detail?.length > 0 && (
                    <Table
                      dataSource={ops.calls_detail.map((c, i) => ({ ...c, key: i }))}
                      columns={[
                        { title: '#', key: 'idx', width: 40, render: (_, __, i) => i + 1 },
                        { title: 'Purpose', dataIndex: 'purpose', key: 'purpose',
                          render: (v) => <Tag>{v}</Tag> },
                        { title: 'Model', dataIndex: 'model', key: 'model',
                          render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
                        { title: 'Prompt', dataIndex: 'prompt_tokens', key: 'pt', align: 'right',
                          render: (v) => v?.toLocaleString() },
                        { title: 'Completion', dataIndex: 'completion_tokens', key: 'ct', align: 'right',
                          render: (v) => v?.toLocaleString() },
                        { title: 'Cost', dataIndex: 'cost_usd', key: 'cost', align: 'right',
                          render: (v) => `$${v?.toFixed(5)}` },
                      ]}
                      pagination={false}
                      size="small"
                      style={{ marginTop: 16 }}
                    />
                  )}

                  {Object.keys(sr).length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        <Target size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                        Success Rates
                      </Text>
                      <Row gutter={[16, 8]}>
                        <Col xs={12} sm={8}><Text type="secondary">Companies: </Text>{rateTag(sr.companies_identified)}</Col>
                        <Col xs={12} sm={8}><Text type="secondary">Tickers: </Text>{rateTag(sr.tickers_identified)}</Col>
                        <Col xs={12} sm={8}><Text type="secondary">Positions: </Text>{rateTag(sr.positions_identified)}</Col>
                        <Col xs={12} sm={8}><Text type="secondary">Currencies: </Text>{rateTag(sr.currencies_identified)}</Col>
                        <Col xs={12} sm={8}><Text type="secondary">Asset Types: </Text>{rateTag(sr.asset_types_identified)}</Col>
                      </Row>
                    </div>
                  )}

                  {result._meta && (
                    <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 11 }}>
                      Approach: {result._meta.approach}
                      {result._meta.total_rows_in_file && ` | Rows in file: ${result._meta.total_rows_in_file}`}
                    </Text>
                  )}
                </Card>
              );
            })()}

          </>
        )}
      </div>
    </div>
  );
};

export default PortfolioAnalysis;
